import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Bug,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ChevronRight,
  MessageSquare
} from 'lucide-react';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

// Bug status configuration
const BUG_STATUS_CONFIG = {
  new: { 
    label: 'Nouveau', 
    color: 'bg-blue-100 text-blue-700',
    icon: AlertCircle
  },
  in_progress: { 
    label: 'En cours', 
    color: 'bg-amber-100 text-amber-700',
    icon: Clock
  },
  resolved: { 
    label: 'Résolu', 
    color: 'bg-green-100 text-green-700',
    icon: CheckCircle2
  },
  closed: { 
    label: 'Fermé', 
    color: 'bg-slate-100 text-slate-700',
    icon: CheckCircle2
  },
  wont_fix: { 
    label: 'Non corrigé', 
    color: 'bg-slate-100 text-slate-500',
    icon: AlertCircle
  }
};

const MyBugReportsWidget = ({ maxBugs = 3 }) => {
  const { user } = useAuth();
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchMyBugs();
    }
  }, [user]);

  const fetchMyBugs = async () => {
    try {
      const res = await api.get('/bugs/my-reports');
      setBugs(res.data || []);
    } catch (error) {
      console.error('Error fetching my bugs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return "Hier";
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  // Don't show if no bugs reported
  if (bugs.length === 0) return null;

  // Get recent/active bugs
  const activeBugs = bugs.filter(b => b.status !== 'closed' && b.status !== 'wont_fix');
  const recentBugs = bugs.slice(0, maxBugs);

  // Check if there are any updates (resolved bugs)
  const resolvedBugs = bugs.filter(b => b.status === 'resolved');
  const hasUpdates = resolvedBugs.length > 0;

  return (
    <Card className={cn(
      "border",
      hasUpdates && "border-green-200 bg-green-50/50"
    )} data-testid="my-bug-reports-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Bug className="w-4 h-4 text-red-500" />
            Mes signalements
            {activeBugs.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {activeBugs.length} en cours
              </Badge>
            )}
          </span>
          {hasUpdates && (
            <Badge className="bg-green-500 text-white text-xs">
              {resolvedBugs.length} résolu(s)
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {recentBugs.map((bug) => {
            const statusConfig = BUG_STATUS_CONFIG[bug.status] || BUG_STATUS_CONFIG.new;
            const StatusIcon = statusConfig.icon;
            
            return (
              <div 
                key={bug.id} 
                className="flex items-center gap-3 p-2 rounded-lg bg-white border hover:border-slate-300 transition-colors"
              >
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                  statusConfig.color
                )}>
                  <StatusIcon className="w-4 h-4" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    {bug.title}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span>{formatDate(bug.created_at)}</span>
                    <Badge className={cn("text-[10px]", statusConfig.color)}>
                      {statusConfig.label}
                    </Badge>
                  </div>
                </div>
                
                {/* Resolution notes indicator */}
                {bug.resolution_notes && (
                  <div className="flex-shrink-0" title="Note de résolution">
                    <MessageSquare className="w-4 h-4 text-green-500" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {bugs.length > maxBugs && (
          <Button variant="ghost" size="sm" className="w-full mt-2 text-xs">
            Voir tous ({bugs.length})
            <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default MyBugReportsWidget;
